import { API_URL } from '~/config/environment';

import { getLocalStorageItem } from './local-storage';

type FetchOptions = Omit<RequestInit, 'headers' | 'body'> & {
  headers?: Record<string, string>;
  // biome-ignore lint/suspicious/noExplicitAny: body can contain any type of data for API requests
  body?: Record<string, any> | FormData;
  auth?: boolean; // default: true
};

type FetchResult<T> = {
  data: T | null;
  error: string | null;
  status: number;
};

export async function fetchJson<T>(
  url: string,
  options: FetchOptions = {},
): Promise<FetchResult<T>> {
  const { headers = {}, auth = true, body, ...restOptions } = options;

  try {
    const token = auth ? getLocalStorageItem('access_token') : null;

    const finalUrl = `${API_URL}${url}`;
    const isFormData = body instanceof FormData;
    const response = await fetch(finalUrl, {
      ...restOptions,
      headers: {
        ...(body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers || {}),
      },
      ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    });

    const isJson = response.headers
      .get('content-type')
      ?.includes('application/json');

    const responseData = isJson
      ? await response.json().catch(() => ({}))
      : null;

    if (!response.ok) {
      return {
        data: null,
        error:
          responseData?.message ||
          `${response.statusText} (${response.status})`,
        status: response.status,
      };
    }

    return {
      data: responseData as T,
      error: null,
      status: response.status,
    };
  } catch (err) {
    const errorMessage =
      err instanceof DOMException && err.name === 'AbortError'
        ? 'La petición tardó demasiado y fue cancelada'
        : err instanceof Error
          ? err.message
          : 'Ocurrió un error inesperado';

    if (import.meta.env.DEV) {
      console.error('[fetchJson error]', err);
    }

    return {
      data: null,
      error: errorMessage,
      status: 500,
    };
  }
}
